import { FC, SyntheticEvent, useCallback, useContext, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  EProductTemperature,
  IProduct,
  IProductCategory,
  IProductsListFilters,
} from "../types";
import { ColumnType } from "antd/lib/table";
import { actions } from "../slice";
import { AppDispatch } from "../../../app/store";
import { ApiStatuses, SortTypes } from "../../../app/types";
import { LMTable } from "../../../components/LMTable";
import { extendByMetadata } from "../utils";
import { ProductStatus } from "../ProductStatus";
import { approveProducts } from "../api";
import { useAppNotifications, getErrorMessage } from "../../../components/LMNotifications";
import { AbilityContext } from "../../casl";
import { ProductPhotoCell } from "../../../components";
import { getTranslation } from "../../i18n";

interface Props {
  list: IProduct[];
  status: ApiStatuses;
  onSelectProduct: (productId: number) => void;
  filters: IProductsListFilters;
  categories: IProductCategory[];
}

export const ProductsPortfolioList: FC<Props> = ({
  list,
  status,
  onSelectProduct,
  filters,
  categories,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation("productsPortfolio");
  const { contextHolder, success, error } = useAppNotifications();
  const ability = useContext(AbilityContext);

  const handlePageChange = (updatedPage: number) => {
    dispatch(actions.setPage(updatedPage - 1));
  };

  const handlePageSizeChange = (updatedSize: string) => {
    dispatch(actions.setPageSize(Number(updatedSize)));
    dispatch(actions.setPage(0));
  };

  const handleSortNameChange = (column: string, order: SortTypes) => {
    if (column === "name") {
      dispatch(actions.setNameSort(order));
    }
  };

  const handleApproveProduct = useCallback(
    async (evt: SyntheticEvent, id: number) => {
      evt.preventDefault();
      evt.stopPropagation();
      try {
        await approveProducts([id]);
        success(t("approveProductSuccess"));
      } catch (e) {
        error(t("approveProductFail"), getErrorMessage(e));
      }
    },
    [error, success, t]
  );

  // const handleApprovePendingProducts = useCallback(async () => {
  //   const pendingProductsIds = list
  //     .filter((p) => p.status === EProductStatus.Pending)
  //     .map((p) => p.id);
  //   try {
  //     await approveProducts(pendingProductsIds);
  //     success(t("approveProductsSuccess"));
  //   } catch {
  //     error(t("approveProductsFail"));
  //   }
  // }, [error, success, t, list]);

  // const getPendingProductsCount = useCallback(() => {
  //   return list.filter((p) => p.status === EProductStatus.Pending).length;
  // }, [list]);

  const columns = useMemo(
    () => [
      {
        title: t("name"),
        key: "name",
        width: 600,
        render: (product: IProduct) => {
          const { images } = product;
          const lastImage = images[images.length - 1];
          return (
            <ProductPhotoCell
              name={getTranslation(product.name) || ""}
              image={lastImage ? lastImage.url : undefined}
            />
          );
        },
      },
      {
        title: t("status"),
        key: "status",
        render: (product: IProduct) => {
          return <ProductStatus status={product.status} />;
        },
      },
      {
        title: t("categoryName"),
        key: "category",
        render: (product: IProduct) => {
          if (!product.categoryId) return "—";
          const category = categories.find(
            (c) => c.id === product.categoryId
          );
          return category ? getTranslation(category.name) : "—";
        },
      },
      {
        title: t("defaultPrice"),
        key: "defaultPrice",
        render: (product: IProduct) => {
          return product.price?.default?.price ?? "—";
        },
      },
      {
        title: t("priceSublines"),
        key: "priceSublines",
        render: (product: IProduct) => {
          const sublines = product.price?.default?.sublines;
          if (!sublines || sublines.length === 0) return "—";
          return sublines
            .map((s) => `${s.description}: ${s.amount}`)
            .join(", ");
        },
      },
      ...extendByMetadata(list).metadataList.map((metadataName) => ({
        title: metadataName.title,
        key: metadataName.value,
        dataIndex: metadataName.value,
      })),
      // {
      //   title: t("external"),
      //   key: "external",
      //   dataIndex: "external",
      //   render: (external: boolean) => {
      //     return external ? (
      //       <div className={styles.external}>
      //         <IconCheck stroke="var(--color-primary-80)" />
      //       </div>
      //     ) : (
      //       <></>
      //     );
      //   },
      // },
      {
        title: t("temperature"),
        key: "temperature",
        dataIndex: "temperature",
        render: (temperature: EProductTemperature) => {
          return t(temperature);
        },
      },
      // {
      //   title: t("sku"),
      //   key: "skus",
      //   width: 150,
      //   dataIndex: "skus",
      //   render: (skus: IProductSku[]) => {
      //     return <ProductSkusList skus={skus.slice().sort(sortSkus)} />;
      //   },
      // },
      // {
      //   title: "",
      //   key: "action",
      //   render: (product: IProduct) => {
      //     if (product.status === EProductStatus.Pending) {
      //       return (
      //         <Can I="admin" a="products">
      //           <LMIconButton
      //             color="white"
      //             onClick={(evt: SyntheticEvent) =>
      //               handleApproveProduct(evt, product.id)
      //             }
      //           >
      //             <IconCheck />
      //           </LMIconButton>
      //         </Can>
      //       );
      //     }
      //     return null;
      //   },
      // },
    ],
    [t, handleApproveProduct, list, categories]
  ) as ColumnType<IProduct>[];

  const onRow = (order: IProduct) => ({
    onClick: () =>
      ability.can("update", "products") ? onSelectProduct(order.id) : null,
    style: {
      cursor: ability.can("update", "products") ? "pointer" : "initial",
    },
  });

  return (
    <>
      {contextHolder}
      <LMTable
        // action={
        //   getPendingProductsCount() === 0 ? undefined : (
        //     <Can I="admin" a="products">
        //       <LMNewButton
        //         color="transparent"
        //         className={styles.button}
        //         onClick={handleApprovePendingProducts}
        //       >
        //         {t("approvePendingProducts")}{" "}
        //         <Button1 className={styles.label}>
        //           {getPendingProductsCount()}
        //         </Button1>
        //       </LMNewButton>
        //     </Can>
        //   )
        // }
        loading={status === ApiStatuses.loading}
        columns={columns}
        dataSource={extendByMetadata(list).productsWithMetadata}
        total={{ title: t("products"), amount: filters.totalItems }}
        onChangePageSize={handlePageSizeChange}
        onChangePage={handlePageChange}
        onSortChange={handleSortNameChange}
        pageSize={filters.pageSize}
        page={filters.page + 1}
        onRow={onRow}
        scroll={{ x: 1300 }}
      />
    </>
  );
};
